import React, { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/App";
import api from "@/lib/api";
import {
  UserCog, Plus, Trash2, Sun, Moon, Sunset, Calendar,
  ChevronLeft, ChevronRight, List, LayoutGrid
} from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
  DialogTrigger, DialogDescription
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "@/components/ui/select";
import { toast } from "sonner";

const SHIFT_ICONS = { manana: Sun, tarde: Sunset, noche: Moon };
const SHIFT_COLORS = [
  { bg: "bg-amber-50", text: "text-amber-700", border: "border-amber-200", dot: "bg-amber-400" },
  { bg: "bg-rose-50", text: "text-rose-600", border: "border-rose-200", dot: "bg-rose-400" },
  { bg: "bg-violet-50", text: "text-violet-700", border: "border-violet-200", dot: "bg-violet-400" },
  { bg: "bg-emerald-50", text: "text-emerald-700", border: "border-emerald-200", dot: "bg-emerald-400" },
  { bg: "bg-sky-50", text: "text-sky-700", border: "border-sky-200", dot: "bg-sky-400" },
  { bg: "bg-orange-50", text: "text-orange-700", border: "border-orange-200", dot: "bg-orange-400" },
];

const roleLabels = {
  director_medico: "Director Médico",
  enfermero: "Enfermero/a",
  medico: "Médico/a",
  admin: "Administrador",
};

const dayNames = ["Lun", "Mar", "Mié", "Jue", "Vie", "Sáb", "Dom"];

function getWeekDates(dateStr) {
  const d = new Date(dateStr + "T12:00:00");
  const day = d.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  const monday = new Date(d);
  monday.setDate(d.getDate() + diff);
  const dates = [];
  for (let i = 0; i < 7; i++) {
    const date = new Date(monday);
    date.setDate(monday.getDate() + i);
    dates.push(date.toISOString().split("T")[0]);
  }
  return dates;
}

function shiftWeek(dateStr, offset) {
  const d = new Date(dateStr + "T12:00:00");
  d.setDate(d.getDate() + offset * 7);
  return d.toISOString().split("T")[0];
}

export default function StaffPage() {
  const { user } = useAuth();
  const canManage = user?.role === "super_admin" || user?.role === "tenant_admin" || user?.permissions?.includes("gestionar_usuarios");
  const [staff, setStaff] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [weekStart, setWeekStart] = useState(new Date().toISOString().split("T")[0]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("calendar");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({ staff_id: "", shift_type: "", shift_date: "", notes: "" });
  const [shiftsConfig, setShiftsConfig] = useState([]);

  const weekDates = getWeekDates(weekStart);
  const weekFrom = weekDates[0];
  const weekTo = weekDates[6];

  const fetchData = useCallback(async () => {
    try {
      const [staffRes, shiftsRes, configRes] = await Promise.all([
        api.get("/staff"),
        api.get("/staff/shifts", { params: { date_from: weekFrom, date_to: weekTo } }),
        api.get("/shifts-config")
      ]);
      setStaff(staffRes.data);
      setShifts(shiftsRes.data);
      setShiftsConfig(configRes.data);
      if (configRes.data.length > 0 && !form.shift_type) {
        setForm(prev => ({ ...prev, shift_type: configRes.data[0].key }));
      }
    } catch { /* ignore */ }
    setLoading(false);
  }, [weekFrom, weekTo]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleCreateShift = async (e) => {
    e.preventDefault();
    const staffMember = staff.find(s => s.id === form.staff_id);
    if (!staffMember) return;
    try {
      await api.post("/staff/shifts", {
        staff_id: form.staff_id,
        staff_name: staffMember.name,
        shift_date: form.shift_date || weekDates[0],
        shift_type: form.shift_type,
        role: staffMember.role,
        notes: form.notes,
      });
      toast.success("Turno asignado");
      setDialogOpen(false);
      setForm({ staff_id: "", shift_type: shiftsConfig[0]?.key || "manana", shift_date: "", notes: "" });
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
  };

  const handleDeleteShift = async (shiftId) => {
    try {
      await api.delete(`/staff/shifts/${shiftId}`);
      toast.success("Turno eliminado");
      fetchData();
    } catch { toast.error("Error"); }
  };

  const openNewShift = (date, shiftType) => {
    setForm({ staff_id: "", shift_type: shiftType || shiftsConfig[0]?.key || "manana", shift_date: date, notes: "" });
    setDialogOpen(true);
  };

  const today = new Date().toISOString().split("T")[0];

  // Build dynamic shift config map
  const shiftConfigMap = {};
  shiftsConfig.forEach((s, i) => {
    const colorIdx = i % SHIFT_COLORS.length;
    const icon = SHIFT_ICONS[s.key] || (i === 0 ? Sun : i === 1 ? Sunset : Moon);
    shiftConfigMap[s.key] = {
      label: s.label,
      icon,
      time: `${s.start}-${s.end}`,
      ...SHIFT_COLORS[colorIdx],
    };
  });

  const weekLabel = (() => {
    const from = new Date(weekFrom + "T12:00:00");
    const to = new Date(weekTo + "T12:00:00");
    const fromStr = from.toLocaleDateString("es-AR", { day: "numeric", month: "short" });
    const toStr = to.toLocaleDateString("es-AR", { day: "numeric", month: "short", year: "numeric" });
    return `${fromStr} — ${toStr}`;
  })();

  return (
    <div className="space-y-6 animate-fade-in-up" data-testid="staff-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight font-[Manrope]">Personal y Turnos</h1>
          <p className="text-sm text-slate-400 mt-0.5">{staff.length} miembros del equipo</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex bg-slate-100 rounded-xl p-0.5">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setView("calendar")}
              className={`rounded-lg h-8 px-3 text-sm ${view === "calendar" ? "bg-white shadow-sm text-slate-900" : "text-slate-500"}`}
              data-testid="view-calendar-btn"
            >
              <LayoutGrid className="w-4 h-4 mr-1.5" /> Calendario
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setView("list")}
              className={`rounded-lg h-8 px-3 text-sm ${view === "list" ? "bg-white shadow-sm text-slate-900" : "text-slate-500"}`}
              data-testid="view-list-btn"
            >
              <List className="w-4 h-4 mr-1.5" /> Lista
            </Button>
          </div>
          {canManage && (
            <Button
              onClick={() => openNewShift(today, shiftsConfig[0]?.key || "manana")}
              className="rounded-xl px-5 h-10 bg-violet-600 text-white hover:bg-violet-700 active:scale-[0.98] transition-all text-sm font-semibold"
              data-testid="add-shift-btn"
            >
              <Plus className="w-4 h-4 mr-2" /> Asignar Turno
            </Button>
          )}
        </div>
      </div>

      {/* Week navigation */}
      <Card className="rounded-2xl border-slate-100 shadow-sm">
        <CardContent className="p-4 flex items-center justify-between">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => { setWeekStart(shiftWeek(weekStart, -1)); setLoading(true); }}
            className="rounded-xl w-9 h-9"
            data-testid="prev-week-btn"
          >
            <ChevronLeft className="w-5 h-5 text-slate-600" />
          </Button>
          <div className="text-center">
            <p className="font-semibold text-slate-900 text-sm">{weekLabel}</p>
            <p className="text-xs text-slate-400">{shifts.length} turnos asignados</p>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => { setWeekStart(shiftWeek(weekStart, 1)); setLoading(true); }}
            className="rounded-xl w-9 h-9"
            data-testid="next-week-btn"
          >
            <ChevronRight className="w-5 h-5 text-slate-600" />
          </Button>
        </CardContent>
      </Card>

      {loading ? (
        <p className="text-slate-400 text-center py-12">Cargando turnos...</p>
      ) : view === "calendar" ? (
        /* ==================== CALENDAR VIEW ==================== */
        <div className="overflow-x-auto" data-testid="calendar-view">
          <div className="min-w-[700px]">
            {/* Header row */}
            <div className="grid grid-cols-[80px_repeat(7,1fr)] gap-1 mb-1">
              <div />
              {weekDates.map((d, i) => {
                const date = new Date(d + "T12:00:00");
                const isToday = d === today;
                return (
                  <div key={d} className={`text-center py-2 rounded-xl ${isToday ? "bg-violet-600 text-white" : "bg-slate-50"}`}>
                    <p className={`text-xs font-semibold ${isToday ? "text-violet-200" : "text-slate-400"}`}>{dayNames[i]}</p>
                    <p className={`text-sm font-bold ${isToday ? "text-white" : "text-slate-700"}`}>{date.getDate()}</p>
                  </div>
                );
              })}
            </div>
            {/* Shift rows */}
            {shiftsConfig.map(sc => sc.key).map(shiftType => {
              const cfg = shiftConfigMap[shiftType];
              if (!cfg) return null;
              const ShiftIcon = cfg.icon;
              return (
                <div key={shiftType} className="grid grid-cols-[80px_repeat(7,1fr)] gap-1 mb-1">
                  <div className={`flex items-center justify-center rounded-xl ${cfg.bg} px-2 py-3`}>
                    <div className="text-center">
                      <ShiftIcon className={`w-4 h-4 ${cfg.text} mx-auto`} strokeWidth={1.8} />
                      <p className={`text-[10px] font-semibold mt-0.5 ${cfg.text}`}>{cfg.time}</p>
                    </div>
                  </div>
                  {weekDates.map(d => {
                    const cellShifts = shifts.filter(s => s.shift_date === d && s.shift_type === shiftType);
                    return (
                      <div
                        key={d}
                        className={`min-h-[70px] rounded-xl border border-slate-100 p-1.5 transition-all hover:border-slate-200 ${
                          canManage ? "cursor-pointer hover:bg-slate-50/50" : ""
                        } ${d === today ? "border-violet-200 bg-violet-50/20" : ""}`}
                        onClick={() => canManage && cellShifts.length === 0 && openNewShift(d, shiftType)}
                        data-testid={`cell-${d}-${shiftType}`}
                      >
                        {cellShifts.length === 0 ? (
                          canManage && (
                            <div className="h-full flex items-center justify-center opacity-0 hover:opacity-40 transition-opacity">
                              <Plus className="w-4 h-4 text-slate-400" />
                            </div>
                          )
                        ) : (
                          <div className="space-y-1">
                            {cellShifts.map(s => (
                              <div
                                key={s.id}
                                className={`${cfg.bg} rounded-lg px-2 py-1.5 group relative`}
                                data-testid={`cal-shift-${s.id}`}
                              >
                                <p className={`text-[11px] font-semibold ${cfg.text} leading-tight truncate`}>{s.staff_name}</p>
                                <p className="text-[9px] text-slate-400 truncate">{roleLabels[s.role] || s.role}</p>
                                {canManage && (
                                  <button
                                    onClick={e => { e.stopPropagation(); handleDeleteShift(s.id); }}
                                    className="absolute top-1 right-1 w-5 h-5 rounded-full bg-white shadow-sm flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:bg-rose-50"
                                    data-testid={`cal-del-${s.id}`}
                                  >
                                    <Trash2 className="w-3 h-3 text-rose-500" />
                                  </button>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>
        </div>
      ) : (
        /* ==================== LIST VIEW ==================== */
        <div className="space-y-4" data-testid="list-view">
          {weekDates.map(d => {
            const dayShifts = shifts.filter(s => s.shift_date === d);
            if (dayShifts.length === 0) return null;
            const date = new Date(d + "T12:00:00");
            const isToday = d === today;
            return (
              <div key={d}>
                <div className="flex items-center gap-2 mb-2">
                  <p className={`text-sm font-semibold ${isToday ? "text-violet-600" : "text-slate-700"}`}>
                    {date.toLocaleDateString("es-AR", { weekday: "long", day: "numeric", month: "short" })}
                  </p>
                  {isToday && <Badge className="rounded-full bg-violet-50 text-violet-700 border-0 text-[10px] font-semibold">Hoy</Badge>}
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                  {shiftsConfig.map(sc => sc.key).map(st => {
                    const cfg = shiftConfigMap[st];
                    if (!cfg) return null;
                    const typeShifts = dayShifts.filter(s => s.shift_type === st);
                    if (typeShifts.length === 0) return null;
                    return (
                      <Card key={st} className={`rounded-xl border ${cfg.border} ${cfg.bg} shadow-none`}>
                        <CardContent className="p-3">
                          <p className={`text-xs font-semibold ${cfg.text} mb-2`}>{cfg.label}</p>
                          {typeShifts.map(s => (
                            <div key={s.id} className="flex items-center justify-between bg-white/60 rounded-lg px-3 py-2 mb-1" data-testid={`list-shift-${s.id}`}>
                              <div>
                                <p className="text-sm font-medium text-slate-900">{s.staff_name}</p>
                                <p className="text-[10px] text-slate-400">{roleLabels[s.role] || s.role}</p>
                              </div>
                              {canManage && (
                                <Button variant="ghost" size="icon" onClick={() => handleDeleteShift(s.id)} className="rounded-full hover:bg-rose-50 hover:text-rose-500 w-7 h-7">
                                  <Trash2 className="w-3.5 h-3.5" />
                                </Button>
                              )}
                            </div>
                          ))}
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              </div>
            );
          })}
          {shifts.length === 0 && (
            <Card className="rounded-2xl border-slate-100 shadow-sm">
              <CardContent className="p-12 text-center">
                <Calendar className="w-10 h-10 text-slate-300 mx-auto mb-3" strokeWidth={1.5} />
                <p className="text-sm text-slate-400">Sin turnos asignados esta semana</p>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Staff Team */}
      <div>
        <h2 className="text-base font-semibold text-slate-900 mb-3">Equipo de Trabajo</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {staff.map(s => (
            <Card key={s.id} className="rounded-2xl border-slate-100 shadow-sm hover:shadow-md transition-all duration-200" data-testid={`staff-member-${s.id}`}>
              <CardContent className="p-4 flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-violet-50 flex items-center justify-center text-violet-600 font-semibold text-sm flex-shrink-0">
                  {s.name?.charAt(0)}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-slate-900 text-sm truncate">{s.name}</p>
                  <p className="text-xs text-slate-400">{roleLabels[s.role] || s.role}</p>
                  {s.matricula && <p className="text-xs text-slate-400">Mat. {s.matricula}</p>}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>

      {/* Create Shift Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="rounded-2xl sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-bold text-slate-900 font-[Manrope]">Asignar Turno</DialogTitle>
            <DialogDescription className="text-slate-400 text-sm">Seleccionar personal y tipo de turno</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreateShift} className="space-y-4 mt-2">
            <div className="space-y-2">
              <Label className="font-medium text-sm text-slate-700">Fecha</Label>
              <Input
                type="date"
                value={form.shift_date}
                onChange={e => setForm({ ...form, shift_date: e.target.value })}
                className="rounded-xl h-10 bg-slate-50 border-slate-200"
                required
                data-testid="shift-date-input"
              />
            </div>
            <div className="space-y-2">
              <Label className="font-medium text-sm text-slate-700">Personal</Label>
              <Select value={form.staff_id} onValueChange={v => setForm({ ...form, staff_id: v })}>
                <SelectTrigger className="rounded-xl h-10 bg-slate-50 border-slate-200" data-testid="shift-staff-select">
                  <SelectValue placeholder="Seleccionar..." />
                </SelectTrigger>
                <SelectContent>
                  {staff.map(s => (
                    <SelectItem key={s.id} value={s.id}>{s.name} ({roleLabels[s.role] || s.role})</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="font-medium text-sm text-slate-700">Turno</Label>
              <Select value={form.shift_type} onValueChange={v => setForm({ ...form, shift_type: v })}>
                <SelectTrigger className="rounded-xl h-10 bg-slate-50 border-slate-200" data-testid="shift-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {shiftsConfig.map(s => (
                    <SelectItem key={s.key} value={s.key}>{s.label} ({s.start} - {s.end})</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="font-medium text-sm text-slate-700">Notas</Label>
              <Input value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} placeholder="Opcional..." className="rounded-xl h-10 bg-slate-50 border-slate-200" data-testid="shift-notes-input" />
            </div>
            <Button type="submit" disabled={!form.staff_id || !form.shift_date} className="w-full rounded-xl h-10 bg-violet-600 text-white font-semibold hover:bg-violet-700 active:scale-[0.98] transition-all" data-testid="submit-shift-btn">
              Asignar Turno
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
